import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BackLink } from '../../../components/BackLink'
import { DetailField } from '../../../components/DetailField'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { SectionCard, SectionTitle } from '../../../components/SectionCard'
import { StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatCurrency, formatDateTime, useAppToast } from '../../../helpers'
import { EventMachineService } from '../../../services/Event/EventMachineService'
import { EventService } from '../../../services/Event/EventService'
import { EventTerminalPaymentsService } from '../../../services/Event/EventTerminalPaymentsService'
import { EventMachine, EventTerminalPayment, TerminalPaymentMethod } from '../../../services/Event/types'
import PaymentProof, { isPdfProof } from './tabs/PaymentProof'
import { deviceTypeLabels, PANEL_LABEL, PAYMENT_METHOD, TERMINAL_STATUS, voidReasonLabels } from './tabs/tabLabels'

export default function EventTransactionsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const eventService = new EventService()
  const paymentsService = new EventTerminalPaymentsService()
  const machineService = new EventMachineService()

  const [eventId, setEventId] = useState<number | null>(null)
  const [payments, setPayments] = useState<EventTerminalPayment[]>([])
  const [machines, setMachines] = useState<EventMachine[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'approved' | 'voided' | ''>('')
  const [methodFilter, setMethodFilter] = useState<TerminalPaymentMethod | ''>('')
  const [machineFilter, setMachineFilter] = useState<string>('')

  const [selected, setSelected] = useState<EventTerminalPayment | null>(null)
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Discards stale list() responses when filters change in quick succession
  // — without this, a slow response for an older filter could overwrite the
  // fresh one and leave the table out of sync with the controls.
  const requestIdRef = useRef(0)

  useEffect(() => {
    // Slug changed: clear all event-scoped state so we never render stale
    // data from the previous event (machines, filters and selected payment
    // are all event-specific).
    setEventId(null)
    setPayments([])
    setMachines([])
    setTotal(0)
    setPage(1)
    setSearch('')
    setStatusFilter('')
    setMethodFilter('')
    setMachineFilter('')
    setSelected(null)
    onDrawerClose()
    requestIdRef.current++

    eventService
      .getBySlug(slug!)
      .then((data) => setEventId(data.id))
      .catch(() => {
        toast.error('Evento não encontrado')
        navigate('/events')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  useEffect(() => {
    if (eventId === null) return
    machineService
      .listMachines(eventId)
      .then(setMachines)
      .catch(() =>
        toast.warning(
          'Não foi possível carregar os dispositivos',
          'O filtro por máquina ficará indisponível. Recarregue a página para tentar de novo.'
        )
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  useEffect(() => {
    if (eventId === null) return
    const timeout = setTimeout(fetchPayments, 400)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, search, statusFilter, methodFilter, machineFilter, page, pageSize])

  function fetchPayments() {
    if (eventId === null) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setLoadError(false)
    paymentsService
      .list(eventId, {
        search: search || undefined,
        status: statusFilter || 'all',
        method: methodFilter || undefined,
        machine_id: machineFilter ? parseInt(machineFilter, 10) : undefined,
        page,
        page_size: pageSize
      })
      .then((res) => {
        if (reqId !== requestIdRef.current) return
        setPayments(res.data)
        setTotal(res.total)
      })
      .catch((error) => {
        if (reqId !== requestIdRef.current) return
        setLoadError(true)
        toast.error('Erro ao carregar os pagamentos', error)
      })
      .finally(() => {
        if (reqId !== requestIdRef.current) return
        setLoading(false)
      })
  }

  function handleRowClick(payment: EventTerminalPayment) {
    setSelected(payment)
    onDrawerOpen()
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <>
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={5}
      >
        <BackLink to={`/events/${slug}/admin`}>Voltar para o Evento</BackLink>
      </Flex>

      <PageTitle title="Pagamentos no Local" description="Cobranças feitas nas maquininhas do evento" mb={5} />

      <SectionCard>
        <FilterBar
          onClear={() => {
            setSearch('')
            setStatusFilter('')
            setMethodFilter('')
            setMachineFilter('')
            setPage(1)
          }}
          isDirty={search !== '' || statusFilter !== '' || methodFilter !== '' || machineFilter !== ''}
        >
          <FilterSearch
            value={search}
            onChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
            placeholder="Buscar por nome ou mesa..."
            minW={{ md: '240px' }}
          />
          <FilterSelect
            label="Status"
            maxW={{ md: '180px' }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as 'approved' | 'voided' | '')
              setPage(1)
            }}
            options={[
              { value: 'approved', label: 'Aprovados' },
              { value: 'voided', label: 'Estornados' }
            ]}
          />
          <FilterSelect
            label="Método"
            maxW={{ md: '200px' }}
            value={methodFilter}
            onChange={(value) => {
              setMethodFilter(value as TerminalPaymentMethod | '')
              setPage(1)
            }}
            options={Object.entries(PAYMENT_METHOD).map(([value, { label }]) => ({ value, label }))}
            emptyLabel="Todos os métodos"
          />
          <FilterSelect
            label="Maquininha"
            maxW={{ md: '220px' }}
            value={machineFilter}
            onChange={(value) => {
              setMachineFilter(value)
              setPage(1)
            }}
            options={machines.map((m) => ({
              value: String(m.id),
              label: `${m.name} (${deviceTypeLabels[m.device_type] ?? m.device_type})`
            }))}
            emptyLabel="Todas as máquinas"
          />
        </FilterBar>

        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Data/Hora</Th>
                <Th>Cliente</Th>
                <Th>Mesa</Th>
                <Th isNumeric>Valor</Th>
                <Th>Método</Th>
                <Th>Máquina</Th>
                <Th>Status</Th>
                <Th>Comprovante</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <TableSkeletonRows cols={8} rows={8} />
              ) : loadError ? (
                <TableErrorRow colSpan={8} entity="pagamentos" onRetry={fetchPayments} />
              ) : payments.length === 0 ? (
                <TableEmptyRow colSpan={8} entity="pagamento" />
              ) : (
                payments.map((p) => {
                  return (
                    <Tr
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                      onClick={() => handleRowClick(p)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleRowClick(p)
                        }
                      }}
                    >
                      <Td>
                        <Text variant="caption">{p.paid_at ? formatDateTime(p.paid_at) : '-'}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" fontWeight="500" color="gray.800">
                          {p.customer_name ?? '-'}
                        </Text>
                      </Td>
                      <Td>
                        <Text variant="caption" fontFamily="mono">
                          {p.tab_code}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="gray.700">
                          {formatCurrency(p.amount)}
                        </Text>
                      </Td>
                      <Td>
                        <StatusBadge status={p.method} statuses={PAYMENT_METHOD} />
                      </Td>
                      <Td>
                        <Text variant="caption">{p.machine_name ?? (p.origin === 'panel' ? PANEL_LABEL : '-')}</Text>
                      </Td>
                      <Td>
                        <StatusBadge status={p.status} statuses={TERMINAL_STATUS} />
                      </Td>
                      <Td>
                        {p.is_manual && p.proof_image_url ? (
                          <Icon
                            name={isPdfProof(p.proof_image_url) ? 'lni lni-empty-file' : 'lni lni-image'}
                            color="purple.500"
                            size="lg"
                          />
                        ) : (
                          <Text fontSize="xs" color="gray.300">
                            —
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  )
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Paginator
            page={page}
            totalPages={totalPages}
            changePage={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}

        {!loading && payments.length > 0 && (
          <Text variant="caption" mt={3} textAlign="right">
            {total} pagamento{total === 1 ? '' : 's'}
          </Text>
        )}
      </SectionCard>

      <Drawer isOpen={isDrawerOpen} placement="right" onClose={onDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {/* `h2` porque os blocos da gaveta são `SectionTitle variant="mini"`, que emite `h3`:
                sem este nível o outline pularia do `h1` da página direto para eles. */}
            <Heading as="h2" fontWeight="700">
              Detalhes do Pagamento
            </Heading>
            {selected && (
              <Text variant="caption" mt={0.5}>
                {selected.external_reference ? `ATK ${selected.external_reference}` : `#${selected.id}`}
              </Text>
            )}
          </DrawerHeader>
          <DrawerBody>
            {selected && (
              <VStack align="stretch" spacing={5} pt={2}>
                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontSize="lg" fontWeight="700" color="gray.800">
                      {selected.customer_name ?? 'Sem nome'}
                    </Text>
                    <StatusBadge status={selected.status} statuses={TERMINAL_STATUS} />
                  </Flex>
                  <Text fontSize="2xl" fontWeight="700" color="gray.900">
                    {formatCurrency(selected.amount)}
                  </Text>
                </Box>

                <Box>
                  <SectionTitle variant="mini">Pagamento</SectionTitle>
                  <VStack align="stretch" spacing={1}>
                    <DetailField layout="inline" label="Método" value={PAYMENT_METHOD[selected.method].label} />
                    {(selected.method === 'credit' || selected.method === 'credit_manual') && (
                      <DetailField
                        layout="inline"
                        label="Parcelas"
                        value={`${selected.installments}x${selected.has_interest ? ' (com juros)' : ''}`}
                      />
                    )}
                    {/* ABRA-425 — the panel's check-out with payment has no machine. */}
                    {selected.origin === 'panel' ? (
                      <DetailField layout="inline" label="Origem" value="Pagamento manual (painel)" />
                    ) : selected.is_manual ? (
                      <DetailField layout="inline" label="Origem" value="Pagamento manual (celular)" />
                    ) : (
                      <DetailField layout="inline" label="Origem" value="Stone (terminal)" />
                    )}
                    {selected.external_reference && (
                      <DetailField layout="inline" label="ATK Stone" value={selected.external_reference} />
                    )}
                    {selected.payment_method_note && (
                      <DetailField layout="inline" label="Observação" value={selected.payment_method_note} />
                    )}
                    {/* ABRA-317 — parte do pagamento que foi de compras nos estabelecimentos. */}
                    {(selected.establishment_amount ?? 0) > 0 && (
                      <DetailField
                        layout="inline"
                        label="Compras em estabelecimentos"
                        value={formatCurrency(selected.establishment_amount ?? 0)}
                      />
                    )}
                    {/* ABRA-338 — parte do pagamento que foi taxa sistêmica das obras arrematadas. */}
                    {(selected.system_fee_amount ?? 0) > 0 && (
                      <DetailField
                        layout="inline"
                        label="Taxa sistêmica"
                        value={formatCurrency(selected.system_fee_amount ?? 0)}
                      />
                    )}
                  </VStack>
                </Box>

                <Box>
                  <SectionTitle variant="mini">Mesa e Dispositivo</SectionTitle>
                  <VStack align="stretch" spacing={1}>
                    <DetailField layout="inline" label="Mesa (tab_code)" value={selected.tab_code} />
                    <DetailField
                      layout="inline"
                      label="Máquina"
                      value={
                        selected.machine_name
                          ? `${selected.machine_name}${
                              selected.machine_device_type
                                ? ' · ' +
                                  (deviceTypeLabels[selected.machine_device_type] ?? selected.machine_device_type)
                                : ''
                            }`
                          : selected.origin === 'panel'
                            ? PANEL_LABEL
                            : '—'
                      }
                    />
                  </VStack>
                </Box>

                <Box>
                  <SectionTitle variant="mini">Datas</SectionTitle>
                  <VStack align="stretch" spacing={1}>
                    <DetailField
                      layout="inline"
                      label="Pago em"
                      value={selected.paid_at ? formatDateTime(selected.paid_at, { showSeconds: true }) : '—'}
                    />
                    {selected.status === 'voided' && (
                      <>
                        <DetailField
                          layout="inline"
                          label="Estornado em"
                          value={selected.voided_at ? formatDateTime(selected.voided_at, { showSeconds: true }) : '—'}
                        />
                        {selected.void_reason && (
                          <DetailField
                            layout="inline"
                            label="Motivo"
                            value={voidReasonLabels[selected.void_reason] ?? selected.void_reason}
                          />
                        )}
                        {selected.voided_by_machine_name && (
                          <DetailField layout="inline" label="Estornado por" value={selected.voided_by_machine_name} />
                        )}
                      </>
                    )}
                  </VStack>
                </Box>

                {selected.is_manual && selected.proof_image_url && (
                  <Box>
                    <SectionTitle variant="mini">Comprovante</SectionTitle>
                    <PaymentProof url={selected.proof_image_url} />
                  </Box>
                )}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
